// src/pages/AccountsDashboard.jsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import AccountsReport from "../comps/AccountsReport";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
// adjust path if needed

const pad = (n) => String(n).padStart(2, "0");
const getTodayYMD = () => {
  const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
};
const getNowHHMM = () => {
  const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AccountsDashboard() {
  // default totals shapes include the extra keys we expect
  const [labTotals, setLabTotals] = useState({ total:0, paid:0, due:0, rows:0, cash:0, card:0, online:0, discount:0 });
  const [opdTotals, setOpdTotals] = useState({
    total:0, paid:0, due:0, rows:0, cash:0, card:0, online:0, discount:0,
    opd_doctor_fees: 0, opd_reg_fees: 0
  });
   // IPD totals: keep one combined state used by the UI, but collect admitted/discharged separately
  const [ipdAdmittedTotals, setIpdAdmittedTotals] = useState(null);
  const [ipdDischargedTotals, setIpdDischargedTotals] = useState(null);
  const [emergencyTotals, setEmergencyTotals] = useState({ 
    total: 0, 
    paid: 0, 
    due: 0, 
    rows: 0, 
    cash: 0, 
    card: 0, 
    online: 0, 
    discount: 0,
    extra: {} 
  });
  // combined ipd totals (derived)
  const [ipdTotals, setIpdTotals] = useState({
    sum_reg_charges: 0,
    reg_charges_due: 0,
    reg_paid: 0,
    sum_ipd_bill: 0,
    totalAmount: 0,
    sum_paid: 0,
    sum_due: 0,
    count: 0,
    cash: 0,
    card: 0,
    online: 0,
    total: 0,
    paid: 0,
    due: 0,
    rows: 0,
    _raw: {}
  });

  const toNum = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };

  // Normalize helper (small copy of earlier logic) — returns normalized object
  const normalizeIpdTotals = (t = {}) => {
    const sum_reg_charges = toNum(t.sum_reg_charges ?? t.sum_reg ?? t.reg_charges ?? t.reg ?? t.total_reg ?? t.reg_total ?? 0);
    const sum_ipd_bill    = toNum(t.sum_ipd_bill ?? t.sum_ipd ?? t.ipd_total ?? t.ipd_bill_total ?? t.sum_bill ?? 0);
    const sum_paid        = toNum(t.sum_paid ?? t.sum_total_paid ?? t.paid ?? t.total_paid ?? t.paid_amount ?? 0);
    const sum_due         = toNum(t.sum_due ?? t.sum_total_due ?? t.due ?? t.total_due ?? 0);
    const count           = Math.max(0, Math.trunc(toNum(t.count ?? t.rows ?? t.records_count ?? t.total_rows ?? 0)));

    // try to find cash/card/online from common keys
    const firstMatch = (keys) => {
      for (let k of keys) {
        if (Object.prototype.hasOwnProperty.call(t, k)) return toNum(t[k]);
      }
      return 0;
    };
    const cashKeys = ['sum_paid_cash','sum_cash','total_cash','paid_cash','paidCash','cash','total_cash_paid'];
    const cardKeys = ['sum_paid_card','sum_card','total_card','paid_card','paidCard','card','total_card_paid'];
    const onlineKeys = ['sum_paid_online','sum_online','total_online','paid_online','paidOnline','online','total_online_paid'];

    const cash  = firstMatch(cashKeys);
    const card  = firstMatch(cardKeys);
    const online = firstMatch(onlineKeys);

    const reg_charges_due_keylist = ['reg_charges_due','sum_reg_due','sum_reg_charges_due','reg_due','reg_charges_outstanding'];
    let reg_charges_due = null;
    for (let k of reg_charges_due_keylist) {
      if (Object.prototype.hasOwnProperty.call(t, k)) { reg_charges_due = toNum(t[k]); break; }
    }
    if (reg_charges_due === null) {
      // fallback: infer from sum_due and sum_ipd_bill (if available)
      reg_charges_due = sum_due && sum_ipd_bill ? Math.max(0, sum_due - sum_ipd_bill) : 0;
    }

    const reg_paid = Math.max(0, sum_reg_charges - reg_charges_due);
    const totalAmount = sum_reg_charges + sum_ipd_bill;

    return {
      sum_reg_charges,
      reg_charges_due,
      reg_paid,
      sum_ipd_bill,
      totalAmount,
      sum_paid,
      sum_due,
      count,
      cash,
      card,
      online,
      total: totalAmount,
      paid: sum_paid,
      due: sum_due,
      rows: count,
      _raw: t
    };
  };

  // handlers used by each AccountsReport card
  const handleIpdAdmitted = (t = {}) => {
    const normalized = normalizeIpdTotals(t);
    setIpdAdmittedTotals(normalized);
  };
  const handleIpdDischarged = (t = {}) => {
    const normalized = normalizeIpdTotals(t);
    setIpdDischargedTotals(normalized);
  };



  // const exportSummaryWithAutoTable = () => {
  //   try {
  //     const doc = new jsPDF("p", "mm", "a4");
  //     const marginLeft = 14;
  //     const pageWidth = doc.internal.pageSize.getWidth();
  //     const usableWidth = pageWidth - marginLeft * 2;
  
  //     const now = new Date();
  //     const ts = now.toLocaleString();
  
  //     // Title
  //     doc.setFontSize(14);
  //     doc.text("Accounts Dashboard — Summary", marginLeft, 20);
  //     doc.setFontSize(10);
  //     doc.text(`Generated: ${ts}`, marginLeft, 26);
  
  //     // Prepare rows for main totals table
  //     const labTotal = Number(labTotals?.total ?? 0);
  //     const opdTotal = Number(opdTotals?.total ?? 0);
  
  //     // IPD parts
  //     const ipdReg =  (Number(ipdTotals.sum_paid ?? 0))-
  //     (Number(ipdTotals.sum_ipd_bill ?? 0));
  //     const ipdBill = Number(ipdTotals?.sum_ipd_bill ?? 0);
  //     const ipdCombined = ipdReg + ipdBill;
  
  //     const pharmaTotal = Number(pharmaTotals?.total ?? 0);
  
  //     const grandTotal = labTotal + opdTotal + ipdCombined + pharmaTotal;
  
  //     const rows = [
  //       ["Lab (Total)", `${labTotal.toFixed(2)}`],
  //       ["OPD (Total)", `${opdTotal.toFixed(2)}`],
  //       ["IPD — Reg Charges", `${ipdReg.toFixed(2)}`],
  //       ["IPD — IPD Bill", `${ipdBill.toFixed(2)}`],
  //       ["IPD — (Reg + IPD)", `${ipdCombined.toFixed(2)}`],
  //       ["Pharmacy (Total)", `${pharmaTotal.toFixed(2)}`],
  //       [{ content: "GRAND TOTAL", styles: { fillColor: [220,220,220], halign: "center" } }, { content: `${grandTotal.toFixed(2)}`, styles: { fillColor: [220,220,220], halign: "right" } }]
  //     ];
  
  //     // Add main totals table
  //     autoTable(doc, {
  //       startY: 34,
  //       theme: "grid",
  //       head: [["Item", "Amount"]],
  //       body: rows,
  //       styles: { fontSize: 10 },
  //       headStyles: { fillColor: [60, 130, 200], textColor: 255, halign: "center" },
  //       bodyStyles: {
  //         fontStyle: "bold", // make amount column bold
  //         textColor: [0, 0, 0]
  //       },
  //       columnStyles: {
  //         0: { cellWidth: usableWidth * 0.6, halign: "left" },
  //         1: { cellWidth: usableWidth * 0.4, halign: "center", fontStyle: "bold" }
  //       },
  //       tableWidth: usableWidth,
  //     });
      
  
  //     // Payment breakdown (cash/card/online/paid/due) table
  //     const afterY = doc.lastAutoTable.finalY + 8;
  //     const cash = Number(labTotals?.cash ?? 0) + Number(opdTotals?.cash ?? 0) + Number(ipdTotals?.cash ?? 0) + Number(pharmaTotals?.cash ?? 0);
  //     const card = Number(labTotals?.card ?? 0) + Number(opdTotals?.card ?? 0) + Number(ipdTotals?.card ?? 0) + Number(pharmaTotals?.card ?? 0);
  //     const online = Number(labTotals?.online ?? 0) + Number(opdTotals?.online ?? 0) + Number(ipdTotals?.online ?? 0) + Number(pharmaTotals?.online ?? 0);
  //     const totalPaid = Number(combined?.paid ?? (labTotals?.paid||0) + (opdTotals?.paid||0) + (ipdTotals?.sum_paid||0) + (pharmaTotals?.paid||0));
  //     const totalDue = Number(combined?.due ?? (labTotals?.due||0) + (opdTotals?.due||0) + (ipdTotals?.sum_due||0) + (pharmaTotals?.due||0));
  
  //     const paymentRows = [
  //       ["Cash Paid", `${cash.toFixed(2)}`],
  //       ["Card Paid", `${card.toFixed(2)}`],
  //       ["Online Paid", `${online.toFixed(2)}`],
  //       ["Total Paid", `${totalPaid.toFixed(2)}`],
  //       ["Total Due", `${totalDue.toFixed(2)}`]
  //     ];
  
  //     autoTable(doc, {
  //       startY: afterY,
  //       theme: "grid", // use grid to maintain borders
  //       head: [["Payment Method", "Amount"]],
  //       body: paymentRows,
  //       styles: { fontSize: 10 },
  //       headStyles: { fillColor: [100, 100, 100], textColor: 255, halign: "center" },
  //       bodyStyles: {
  //         fontStyle: "bold",
  //         textColor: [0, 0, 0],
  //       },
  //       columnStyles: {
  //         0: { cellWidth: usableWidth * 0.6, halign: "left" },
  //         1: { cellWidth: usableWidth * 0.4, halign: "center", fontStyle: "bold" }
  //       },
  //       tableWidth: usableWidth,
  //     });
      
  
  //     // Footer note
  //     const footY = doc.lastAutoTable.finalY + 12;
  //     doc.setFontSize(9);
  //     doc.setTextColor(100);
  //     doc.text("Note: Numbers reflect the current dashboard filters and visible rows.", marginLeft, footY);
  
  //     // Save file with timestampized name
  //     const filename = `accounts-summary-${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}_${String(now.getHours()).padStart(2,"0")}${String(now.getMinutes()).padStart(2,"0")}.pdf`;
  //     doc.save(filename);
  //   } catch (err) {
  //     console.error("exportSummaryWithAutoTable error", err);
  //     Swal.fire({ icon: "error", title: "Export failed", text: String(err?.message || err) });
  //   }
  // };
  
  // derive combined ipdTotals whenever admitted/discharged change
  
  
  const exportSummaryWithAutoTable = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const marginLeft = 14;
      const pageWidth = doc.internal.pageSize.getWidth();
      const usableWidth = pageWidth - marginLeft * 2;
  
      const now = new Date();
      const ts = now.toLocaleString();
  
      // Format selected filter range
      const fromDate = filters.from || "";
      const toDate   = filters.to   || "";
      const fromTime = filters.fromTime || "00:00";
      const toTime   = filters.toTime || "23:59";
      const rangeStr = (fromDate || toDate)
        ? `From: ${fromDate} ${fromTime}  —  To: ${toDate} ${toTime}`
        : `All dates`;
  
      // Title + meta
      doc.setFontSize(14);
      doc.text("Accounts Dashboard — Summary", marginLeft, 18);
      doc.setFontSize(10);
      doc.text(`Generated: ${ts}`, marginLeft, 24);
      doc.text(rangeStr, marginLeft, 30);
  
      // ✅ Calculate all totals with emergency, pharmacy and procedure bills included
      const labTotal = Number(labTotals?.total ?? 0);
      const labPaid = Number(labTotals?.paid ?? 0);
      const labDue = Number(labTotals?.due ?? 0);
      
      // OPD: Include procedure bills
      const opdBaseTotal = Number(opdTotals?.total ?? 0);
      const opdBasePaid = Number(opdTotals?.paid ?? 0);
      const opdBaseDue = Number(opdTotals?.due ?? 0);
      const opdProcedureBills = Number(opdTotals?.extra?.["Procedure Bills"] ?? 0);
      const opdTotal = opdBaseTotal + opdProcedureBills;
      const opdPaid = opdBasePaid + opdProcedureBills;
      const opdDue = opdBaseDue;
  
      // ✅ Emergency: Include procedure bills
      const emergencyBaseTotal = Number(emergencyTotals?.total ?? 0);
      const emergencyBasePaid = Number(emergencyTotals?.paid ?? 0);
      const emergencyBaseDue = Number(emergencyTotals?.due ?? 0);
      const emergencyProcedureBills = Number(emergencyTotals?.extra?.["Procedure Bills"] ?? 0);
      const emergencyTotal = emergencyBaseTotal + emergencyProcedureBills;
      const emergencyPaid = emergencyBasePaid + emergencyProcedureBills;
      const emergencyDue = emergencyBaseDue;
  
      // IPD
      const ipdReg = (Number(ipdTotals.sum_paid ?? 0)) - (Number(ipdTotals.sum_ipd_bill ?? 0));
      const ipdBill = Number(ipdTotals?.sum_ipd_bill ?? 0);
      const ipdCombined = ipdReg + ipdBill;
      const ipdPaid = Number(ipdTotals?.sum_paid ?? 0);
      const ipdDue = Number(ipdTotals?.sum_due ?? 0);
  
      // Pharmacy
      const pharmaTotal = Number(pharmaTotals?.total ?? 0);
      const pharmaPaid = Number(pharmaTotals?.paid ?? 0);
      const pharmaDue = Number(pharmaTotals?.due ?? 0);
  
      // ✅ Grand Totals
      const grandTotal = labTotal + opdTotal + emergencyTotal + ipdCombined + pharmaTotal;
      const grandPaid = labPaid + opdPaid + emergencyPaid + ipdPaid + pharmaPaid;
      const grandDue = labDue + opdDue + emergencyDue + ipdDue + pharmaDue;
  
      const rows = [
        ["Lab (Total)", `${labTotal.toFixed(2)}`],
        ["OPD — Base", `${opdBaseTotal.toFixed(2)}`],
        ["OPD — Procedure Bills", `${opdProcedureBills.toFixed(2)}`],
        ["OPD — (Base + Procedure)", `${opdTotal.toFixed(2)}`],
        // ✅ Emergency rows
        ["Emergency — Base", `${emergencyBaseTotal.toFixed(2)}`],
        ["Emergency — Procedure Bills", `${emergencyProcedureBills.toFixed(2)}`],
        ["Emergency — (Base + Procedure)", `${emergencyTotal.toFixed(2)}`],
        ["IPD — Reg Charges", `${ipdReg.toFixed(2)}`],
        ["IPD — IPD Bill", `${ipdBill.toFixed(2)}`],
        ["IPD — (Reg + IPD)", `${ipdCombined.toFixed(2)}`],
        ["Pharmacy (Total)", `${pharmaTotal.toFixed(2)}`],
        // ✅ Grand Total Row
        [
          { content: "GRAND TOTAL", styles: { fillColor: [230,230,230], halign: "left", fontStyle: "bold" } }, 
          { content: `${grandTotal.toFixed(2)}`, styles: { fillColor: [230,230,230], halign: "right", fontStyle: "bold" } }
        ],
        // ✅ Grand Paid Row
        [
          { content: "GRAND PAID AMOUNT", styles: { fillColor: [200,230,200], halign: "left", fontStyle: "bold" } }, 
          { content: `${grandPaid.toFixed(2)}`, styles: { fillColor: [200,230,200], halign: "right", fontStyle: "bold" } }
        ],
        // ✅ Grand Due Row
        [
          { content: "GRAND DUE AMOUNT", styles: { fillColor: [255,220,220], halign: "left", fontStyle: "bold" } }, 
          { content: `${grandDue.toFixed(2)}`, styles: { fillColor: [255,220,220], halign: "right", fontStyle: "bold" } }
        ]
      ];
  
      // Add main totals table
      autoTable(doc, {
        startY: 36,
        theme: "grid",
        head: [["Item", "Amount"]],
        body: rows,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [60, 130, 200], textColor: 255, halign: "center" },
        columnStyles: {
          0: { cellWidth: usableWidth * 0.6, halign: "left" },
          1: { cellWidth: usableWidth * 0.4, halign: "right", fontStyle: "bold" }
        },
        tableWidth: usableWidth,
      });
  
      // ✅ Payment breakdown
      const afterY = doc.lastAutoTable.finalY + 8;
      const cash = Number(labTotals?.cash ?? 0) + 
                   Number(opdTotals?.cash ?? 0) + 
                   Number(emergencyTotals?.cash ?? 0) + 
                   Number(ipdTotals?.cash ?? 0) + 
                   Number(pharmaTotals?.cash ?? 0);
      
      const card = Number(labTotals?.card ?? 0) + 
                   Number(opdTotals?.card ?? 0) + 
                   Number(emergencyTotals?.card ?? 0) + 
                   Number(ipdTotals?.card ?? 0) + 
                   Number(pharmaTotals?.card ?? 0);
      
      const online = Number(labTotals?.online ?? 0) + 
                     Number(opdTotals?.online ?? 0) + 
                     Number(emergencyTotals?.online ?? 0) + 
                     Number(ipdTotals?.online ?? 0) + 
                     Number(pharmaTotals?.online ?? 0);
  
      const paymentRows = [
        ["Cash Paid", `${cash.toFixed(2)}`],
        ["Card Paid", `${card.toFixed(2)}`],
        ["Online Paid", `${online.toFixed(2)}`],
        // ✅ Summary row
        [
          { content: "TOTAL PAID (Cash + Card + Online)", styles: { fillColor: [230,230,230], halign: "left", fontStyle: "bold" } },
          { content: `${grandPaid.toFixed(2)}`, styles: { fillColor: [230,230,230], halign: "right", fontStyle: "bold" } }
        ]
      ];
  
      autoTable(doc, {
        startY: afterY,
        theme: "grid",
        head: [["Payment Method", "Amount"]],
        body: paymentRows,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [100, 100, 100], textColor: 255, halign: "center" },
        columnStyles: {
          0: { cellWidth: usableWidth * 0.6, halign: "left" },
          1: { cellWidth: usableWidth * 0.4, halign: "right", fontStyle: "bold" }
        },
        tableWidth: usableWidth
      });
  
      // Footer note
      const footY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text("Note: Numbers reflect the current dashboard filters and visible rows.", marginLeft, footY);
  
      // Save file with timestampized name
      const filename = `accounts-summary-${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}_${String(now.getHours()).padStart(2,"0")}${String(now.getMinutes()).padStart(2,"0")}.pdf`;
      doc.save(filename);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Export failed", text: String(err?.message || err) });
    }
  };
  
  
  useEffect(() => {
    const a = ipdAdmittedTotals || {};
    const d = ipdDischargedTotals || {};
    const merged = {
      sum_reg_charges: toNum(a.sum_reg_charges) + toNum(d.sum_reg_charges),
      reg_charges_due: toNum(a.reg_charges_due) + toNum(d.reg_charges_due),
      reg_paid: toNum(a.reg_paid) + toNum(d.reg_paid),
      sum_ipd_bill: toNum(a.sum_ipd_bill) + toNum(d.sum_ipd_bill),
      totalAmount: toNum(a.totalAmount) + toNum(d.totalAmount),
      sum_paid: toNum(a.sum_paid) + toNum(d.sum_paid),
      sum_due: toNum(a.sum_due) + toNum(d.sum_due),
      count: Math.trunc(toNum(a.count) + toNum(d.count)),
      cash: toNum(a.cash) + toNum(d.cash),
      card: toNum(a.card) + toNum(d.card),
      online: toNum(a.online) + toNum(d.online),
      total: toNum(a.total) + toNum(d.total),
      paid: toNum(a.paid) + toNum(d.paid),
      due: toNum(a.due) + toNum(d.due),
      rows: Math.trunc(toNum(a.rows) + toNum(d.rows)),
      _raw: { admitted: a._raw ?? a, discharged: d._raw ?? d }
    };
    setIpdTotals(merged);
  }, [ipdAdmittedTotals, ipdDischargedTotals]);

  const [pharmaTotals, setPharmaTotals] = useState({ total:0, paid:0, due:0, rows:0, cash:0, card:0, online:0, discount:0 });

  const setTotalsCallback = useCallback((setter) => (t) => setter(t), []);

  // Filters state — defaults to today's date/time
  const [filters, setFilters] = useState({
    from: getTodayYMD(),
    to: getTodayYMD(),
    fromTime: "00:00",
    toTime: getNowHHMM(),
    userId: "",
    paymentType: "All"
  });

  // user list for dashboard filter (type=user)
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // detect logged in user (try localStorage/currentUser)
  const detectLoggedIn = () => {
    try {
      const raw = localStorage.getItem("currentUser") || localStorage.getItem("user");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed) return null;
      const id = parsed.user_id ?? parsed.id ?? parsed.uid ?? parsed.userId ?? null;
      const name = parsed.fname || parsed.name || parsed.user_display || parsed.username || "";
      if (id) return { id: String(id), name: String(name) };
    } catch (e) {}
    return null;
  };

  useEffect(() => {
    // load dashboard users (type = user)
    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const fd = new FormData();
        fd.append("type", "user");
        fd.append("page_no", "none");
        const url = `${AppConst.BASE_URL.replace(/\/+$/,"")}/${String(AppConst.getUserListUrl).replace(/^\/+/,"")}`;
        const res = await axios.post(url, fd);
        const d = res?.data ?? {};
        const items = Array.isArray(d.records) ? d.records : Array.isArray(d.data) ? d.data : (Array.isArray(d) ? d : []);
        const normalized = items.map(u => ({ id: u.user_id ?? u.id ?? u.uid ?? null, name: u.fname || u.name || u.user_display || u.username || `User#${u.user_id}` }));
        setUsers(normalized.filter(x => x.id !== null));
        // set dashboard filter user to logged-in user if present
        const logged = detectLoggedIn();
        if (logged) setFilters(prev => ({ ...prev, userId: prev.userId || String(logged.id) }));
      } catch (err) {
        console.error("AccountsDashboard loadUsers error", err);
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
    // eslint-disable-next-line
  }, []);

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const fullUrl = (keyOrRel) => {
    const rel = AppConst[keyOrRel] || keyOrRel;
    if (!rel) return undefined;
    return `${AppConst.BASE_URL.replace(/\/+$/,"")}/${String(rel).replace(/^\/+/,"")}`;
  };
  

  const baseFetchParams = useMemo(() => {
    const p = {
      page_no: "none",
      from: filters.from || undefined,
      to: filters.to || undefined,
      from_time: filters.fromTime || undefined,
      to_time: filters.toTime || undefined,
      ipd_user: filters.userId || undefined,
      userId: filters.userId || undefined,
      payment_type: (filters.paymentType && filters.paymentType !== "All") ? filters.paymentType : undefined
    };
    // remove undefined keys
    const out = {};
    Object.entries(p).forEach(([k,v]) => { if (v !== undefined && v !== null && v !== "") out[k] = v; });
    return out;
  }, [filters]);

  const combined = useMemo(() => {
    const labTotal = toNum(labTotals.total);
    const opdBaseTotal = toNum(opdTotals.total);
    const pharmaTotal = toNum(pharmaTotals.total);
  
    // ✅ OPD: Add procedure bills to OPD total
    const opdProcedureBills = toNum(opdTotals.extra?.["Procedure Bills"] || 0);
    const opdTotal = opdBaseTotal + opdProcedureBills;
  
    // ✅ Emergency: Add procedure bills to Emergency total
    const emergencyBaseTotal = toNum(emergencyTotals.total);
    const emergencyProcedureBills = toNum(emergencyTotals.extra?.["Procedure Bills"] || 0);
    const emergencyTotal = emergencyBaseTotal + emergencyProcedureBills;
  
    const ipdReg = toNum(ipdTotals.sum_reg_charges);
    const ipdBill = toNum(ipdTotals.sum_ipd_bill);
    const ipdTotal = ipdReg + ipdBill;
  
    const total = labTotal + opdTotal + emergencyTotal + ipdTotal + pharmaTotal;
  
    // ✅ PAID: Include emergency paid + procedure bills paid
    const paid = toNum(labTotals.paid) + 
                 toNum(opdTotals.paid) + 
                 opdProcedureBills + 
                 toNum(emergencyTotals.paid) + 
                 emergencyProcedureBills + // ✅ Add emergency procedure bills to paid
                 toNum(ipdTotals.sum_paid) + 
                 toNum(pharmaTotals.paid);
  
    const due  = toNum(labTotals.due)  + 
                 toNum(opdTotals.due)  + 
                 toNum(emergencyTotals.due) + 
                 toNum(ipdTotals.sum_due)  + 
                 toNum(pharmaTotals.due);
  
    const rows = toNum(labTotals.rows) + 
                 toNum(opdTotals.rows) + 
                 toNum(emergencyTotals.rows) + 
                 toNum(ipdTotals.rows || ipdTotals.count) + 
                 toNum(pharmaTotals.rows);
  
    const cash = toNum(labTotals.cash) + 
                 toNum(opdTotals.cash) + 
                 toNum(emergencyTotals.cash) + 
                 toNum(ipdTotals.cash) + 
                 toNum(pharmaTotals.cash);
  
    const card = toNum(labTotals.card) + 
                 toNum(opdTotals.card) + 
                 toNum(emergencyTotals.card) + 
                 toNum(ipdTotals.card) + 
                 toNum(pharmaTotals.card);
  
    const online = toNum(labTotals.online) + 
                   toNum(opdTotals.online) + 
                   toNum(emergencyTotals.online) + 
                   toNum(ipdTotals.online) + 
                   toNum(pharmaTotals.online);
  
    const discount = toNum(labTotals.discount) + 
                     toNum(opdTotals.discount) + 
                     toNum(emergencyTotals.discount) + 
                     toNum(ipdTotals.discount) + 
                     toNum(pharmaTotals.discount);
  
    return { 
      total, 
      paid, 
      due, 
      rows, 
      cash, 
      card, 
      online, 
      discount,
      breakdown: { 
        labTotal, 
        opdBaseTotal,
        opdProcedureBills,
        opdTotal,
        emergencyBaseTotal,
        emergencyProcedureBills,
        emergencyTotal, 
        ipdReg, 
        ipdBill, 
        ipdTotal,
        pharmaTotal
      } 
    };
  }, [labTotals, opdTotals, emergencyTotals, ipdTotals, pharmaTotals]);
  // detect logged user to pass into each AccountsReport
  const logged = detectLoggedIn();
  // Normalize totals coming from AccountsReport for IPD
   // --- Normalize/derive IPD totals (put this above return, e.g. after `const logged = detectLoggedIn();`) ---
     // Normalize totals coming from AccountsReport for IPD
  // Normalize totals coming from AccountsReport for IPD
  const handleIpdTotals = (t = {}) => {
    const toNum = (v) => {
      if (v === undefined || v === null || v === '') return 0;
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    };
  
    const sum_reg_charges = toNum(t.sum_reg_charges ?? t.sum_reg ?? t.reg_charges ?? t.reg ?? t.total_reg ?? t.reg_total ?? 0);
    const sum_ipd_bill    = toNum(t.sum_ipd_bill ?? t.sum_ipd ?? t.ipd_total ?? t.ipd_bill_total ?? t.sum_bill ?? 0);
    const sum_paid        = toNum(t.sum_paid ?? t.sum_total_paid ?? t.paid ?? t.total_paid ?? t.paid_amount ?? 0);
    const sum_due         = toNum(t.sum_due ?? t.sum_total_due ?? t.due ?? t.total_due ?? 0);
    const count           = Math.max(0, Math.trunc(toNum(t.count ?? t.rows ?? t.records_count ?? t.total_rows ?? 0)));
  
    // keys to search for cash/card/online — include "total_*" and backend names
    const cashKeys = [
      'sum_paid_cash','sum_cash','total_cash',
      'ipd_payment_history_cash','ipd_payment_history_paid_cash',
      'paid_cash','paidCash','cash'
    ];
    const cardKeys = [
      'sum_paid_card','sum_card','total_card',
      'ipd_payment_history_card','ipd_payment_history_paid_card',
      'paid_card','paidCard','card'
    ];
    const onlineKeys = [
      'sum_paid_online','sum_online','total_online',
      'ipd_payment_history_online','ipd_payment_history_paid_online',
      'paid_online','paidOnline','online'
    ];
  
    const firstMatch = (keys) => {
      for (let k of keys) {
        if (Object.prototype.hasOwnProperty.call(t, k)) return toNum(t[k]);
      }
      return 0;
    };
  
    const cash  = firstMatch(cashKeys);
    const card  = firstMatch(cardKeys);
    const online = firstMatch(onlineKeys);
  
    // --- reg charges due: explicit or inferred ---
    let reg_charges_due = null;
    const explicitRegDueKeys = ['reg_charges_due','sum_reg_due','sum_reg_charges_due','reg_due','reg_charges_outstanding'];
    for (let k of explicitRegDueKeys) {
      if (Object.prototype.hasOwnProperty.call(t, k)) { reg_charges_due = toNum(t[k]); break; }
    }
    if (reg_charges_due === null) {
      if (sum_due && sum_ipd_bill) {
        reg_charges_due = Math.max(0, sum_due - sum_ipd_bill);
      } else {
        reg_charges_due = 0;
      }
    }
  
    const reg_paid = Math.max(0, sum_reg_charges - reg_charges_due);
    const totalAmount = sum_reg_charges + sum_ipd_bill;
  
    const normalized = {
      sum_reg_charges,
      reg_charges_due,
      reg_paid,
      sum_ipd_bill,
      totalAmount,
      sum_paid,
      sum_due,
      count,
      cash,
      card,
      online,
      // include common aliases used elsewhere
      total: totalAmount,
      paid: sum_paid,
      due: sum_due,
      rows: count,
      _raw: t
    };
  
    // also add backwards-compatible method aliases (so combined footer picks them)
    normalized.total_cash = normalized.cash;
    normalized.total_card = normalized.card;
    normalized.total_online = normalized.online;
  
    setIpdTotals(prev => ({ ...prev, ...normalized }));
  };
  




  return (
    <div className="page-content">
      <div className="container-fluid">

        <div className="row mb-3">
          <div className="col-12">
            <h3>Accounts Dashboard</h3>
            <p className="text-muted">All accounts in one place — Lab, OPD, IPD, Pharmacy</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-2">
                <label>From (date)</label>
                <input type="date" className="form-control" name="from" value={filters.from} onChange={onFilterChange} />
              </div>
              <div className="col-md-1">
                <label>From (time)</label>
                <input type="time" className="form-control" name="fromTime" value={filters.fromTime} onChange={onFilterChange} />
              </div>
              <div className="col-md-2">
                <label>To (date)</label>
                <input type="date" className="form-control" name="to" value={filters.to} onChange={onFilterChange} />
              </div>
              <div className="col-md-1">
                <label>To (time)</label>
                <input type="time" className="form-control" name="toTime" value={filters.toTime} onChange={onFilterChange} />
              </div>

              <div className="col-md-3">
                <label>User Filter</label>
                <select name="userId" className="form-select" value={filters.userId} onChange={onFilterChange}>
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                {usersLoading && <small className="text-muted">Loading users…</small>}
              </div>

              <div className="col-md-3">
                <label>Payment</label>
                <select name="paymentType" className="form-select" value={filters.paymentType} onChange={onFilterChange}>
                  <option value="All">All</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="Online">Online</option>
                </select>
              </div>
            </div>
          </div>
         

        </div>
        <button className="btn btn-primary ms-2 py-2" onClick={exportSummaryWithAutoTable}>
  Export Summary PDF
</button>
<div></div>
        {/* Reports row */}
        <div className="row g-3">
          <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
            <AccountsReport
              title="Lab New Reg. Accounts"
              apiUrl={fullUrl("getLabRegListUrl")}
              method="post"
              mapping={{ uhid: "lab_reg_uhid", patient: "lab_reg_patient_name", total: "lab_final_amt", paid: "lab_paid", due: "lab_due", user: "user_display" }}
              fetchParams={baseFetchParams}
              onTotalsChange={setTotalsCallback(setLabTotals)}
              compact={true}
              collectApi="Webapis/collect_account"
              currentUserId={logged?.id}
              currentUserName={logged?.name}
            />
          </div>

          {/* <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
            <AccountsReport
              title="Lab Later Paid Accounts"
              apiUrl={fullUrl("getLabRegListUrl")}
              method="post"
              mapping={{ uhid: "lab_reg_uhid", patient: "lab_reg_patient_name", total: "lab_total", paid: "lab_paid", due: "lab_due", user: "user_display" }}
              fetchParams={baseFetchParams}
              onTotalsChange={setTotalsCallback(setLabTotals)}
              compact={true}
              collectApi="Webapis/collect_account"
              currentUserId={logged?.id}
              currentUserName={logged?.name}
            />
          </div> */}


         

<div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
  <AccountsReport
    title="OPD Accounts"
    apiUrl={fullUrl("getOpdListUrl")}
    method="post"
    mapping={{ 
      uhid: "opd_uhid", 
      patient: "opd_patient_name", 
      total: "opd_total", 
      paid: "opd_paid", 
      due: "opd_due", 
      user: "user_display" 
    }}
    // ✅ Add Procedure Bill to extra sums
    extraSums={{ 
      "Total Reg Fees": "opd_reg_fees", 
      "Doctor Fees": "opd_fees",
      "Procedure Bills": "procedure_bill_amount" // ✅ NEW
    }}
    fetchParams={baseFetchParams}
    onTotalsChange={setTotalsCallback(setOpdTotals)}
    compact={true}
    collectApi="Webapis/collect_account"
    currentUserId={logged?.id}
    currentUserName={logged?.name}
  />
</div>


          
<div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
<AccountsReport
  title="IPD Admitted Accounts"
  apiUrl={fullUrl("getIpdListUrl3")}
  method="post"
  mapping={{
    uhid: "ipd_uhid",
    patient: "ipd_patient_name",
    total: "ipd_total_paid",
    paid:  "ipd_total_paid",
    due:   "ipd_due",
    user:  "user_display"
  }}
  fetchParams={{ ...baseFetchParams }}
  onTotalsChange={handleIpdAdmitted}   // <--- changed
  compact={true}
  collectApi="Webapis/collect_account"
  currentUserId={logged?.id}
  currentUserName={logged?.name}
/>

</div>


<div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
<AccountsReport
  title="IPD Discharge Accounts"
  apiUrl={fullUrl("getIpdDischargedUrl")}
  method="post"
  mapping={{
    uhid: "ipd_uhid",
    patient: "ipd_patient_name",
    total: "ipd_bill_total_final",
    paid:  "ipd_bill_total_paid",
    due:   "ipd_bill_total_due",
    user:  "user_display"
  }}
  fetchParams={{
    ...baseFetchParams,
    status: "1",
    dischargeFrom: filters.from || undefined,
    dischargeTo: filters.to || undefined,
    dischargeFromTime: filters.fromTime || undefined,
    dischargeToTime: filters.toTime || undefined,
    page_no: "none"
  }}
  onTotalsChange={handleIpdDischarged} // <--- changed
  compact={true}
  collectApi="Webapis/collect_account"
  currentUserId={logged?.id}
  currentUserName={logged?.name}
/>
</div>






<div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
  <AccountsReport
    title="Pharmacy Accounts"
    apiUrl={fullUrl("getMedBillListUrl")}
    method="post"
    mapping={{ 
      uhid: "med_bill_ipd_number", 
      patient: "med_bill_patient_number", 
      total: "med_bill_final_amt", 
      paid: "med_bill_paid", 
      due: "med_bill_due", 
      user: "user_display" 
    }}
    fetchParams={{
      med_bill_type: "all",
      page_no: "none",
      // ✅ FIXED: Use from_date/to_date (not from/to)
      from_date: filters.from || undefined,
      to_date: filters.to || undefined,
      // ✅ Add user filter
      med_bill_user: filters.userId || undefined,
      // ✅ Add payment type filter
      payment_type: (filters.paymentType && filters.paymentType !== "All") ? filters.paymentType : undefined
    }}
    onTotalsChange={setTotalsCallback(setPharmaTotals)}
    compact={true}
    collectApi="Webapis/collect_account"
    currentUserId={logged?.id}
    currentUserName={logged?.name}
  />
</div>

          
       {/* ✅ Emergency/DayCare Card */}
<div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
  <AccountsReport
    title="Emergency / DayCare Accounts"
    apiUrl={fullUrl("getEmergencyListUrl")}
    method="post"
    mapping={{ 
      uhid: "ipd_uhid", 
      patient: "ipd_patient_name", 
      total: "ipd_reg_charges", 
      paid: "ipd_paid", 
      due: "ipd_due", 
      user: "user_display" 
    }}
    // ✅ Add Procedure Bill to extra sums
    extraSums={{ 
      "Reg Charges": "ipd_reg_charges",
      "Procedure Bills": "procedure_bill_amount"
    }}
    fetchParams={baseFetchParams}
    onTotalsChange={setTotalsCallback(setEmergencyTotals)}
    compact={true}
    collectApi="Webapis/collect_account"
    currentUserId={logged?.id}
    currentUserName={logged?.name}
  />
</div> 
         
<div className="col-lg-6 col-md-12">
<div className="card p-3">
  <h6 className="mb-2">IPD Summary</h6>

  <div className="row g-2">
    <div className="col-12">
      <small className="text-muted">Total Reg Charges + Advance Payment</small>
      <div>
      <strong style={{ fontSize: '1.25rem' }}>
      ₹{(
        (Number(ipdTotals.sum_paid ?? 0))-
        (Number(ipdTotals.sum_ipd_bill ?? 0))
        
      ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </strong>
      </div>
    </div>

    <div className="col-12 mt-2">
      <small className="text-muted">Total IPD Bill</small>
      <div>
        <strong>
          ₹{(ipdTotals.sum_ipd_bill ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </strong>
      </div>
    </div>

    <div className="col-12 mt-3">
      <small className="text-muted">TOTAL AMOUNT (Reg + Advance + IPD)</small>
      <div>
       

        <strong style={{ fontSize: '1.25rem' }}>₹{(ipdTotals.sum_paid ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
      </div>
    </div>

    

    <div className="col-6 mt-3">
      <small className="text-muted">IPD Bill Due</small>
      <div>
        <strong>₹{(ipdTotals.sum_due ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
      </div>
    </div>
  </div>
</div>

</div>

        </div>

        {/* Aggregated footer */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card p-3">
              <h5>Combined Summary</h5>

              <div className="d-flex flex-wrap gap-3">
                <div><small className="text-muted">Total Amount</small><div><strong> ₹{(combined.paid + combined.due).toFixed(2)}</strong></div></div>
                <div><small className="text-muted">Paid</small><div><strong>₹{combined.paid.toFixed(2)}</strong></div></div>
                <div><small className="text-muted">Due</small><div><strong>₹{combined.due.toFixed(2)}</strong></div></div>
                <div><small className="text-muted">Total Patient</small><div><strong>{combined.rows}</strong></div></div>
              </div>

              <hr />

              <div className="d-flex flex-wrap gap-3">
                <div><small className="text-muted">Cash Paid</small><div><strong>₹{combined.cash.toFixed(2)}</strong></div></div>
                <div><small className="text-muted">Card Paid</small><div><strong>₹{combined.card.toFixed(2)}</strong></div></div>
                <div><small className="text-muted">Online Paid</small><div><strong>₹{combined.online.toFixed(2)}</strong></div></div>
                {/* <div><small className="text-muted">Total Discount</small><div><strong>₹{combined.discount.toFixed(2)}</strong></div></div> */}
              </div>

              <div className="mt-2 text-muted small">
               
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
